import React from "react";

import { DataTableSkeleton, PageHeaderSkeleton, SectionCardSkeleton, SkeletonBlock } from "components/admin";
import {
  SchoolMatchWeekHighlightSkeletonAccent,
  SchoolMatchWeekHighlightSkeletonCard,
  SchoolMatchWeekHighlightsSkeletonGrid,
} from "components/admin/school/styles/school-match-week-highlights.styles";

import {
  PlayerPerformanceFilterActions,
  PlayerPerformanceFilterBar,
  PlayerPerformanceDesktopFilterRow,
  PlayerPerformanceFilterInputRow,
  PlayerPerformanceMobileFilterBlock,
  PlayerPerformanceMasterDetailGrid,
  PlayerPerformancePlayerList,
  PlayerPerformancePlayerPanel,
  PlayerPerformancePlayerPanelBody,
  PlayerPerformancePlayerPanelHeader,
  PlayerPerformanceRecordsPanel,
  PlayerPerformanceSelectedPlayerWrap,
} from "pages/app/workspace/sections/styles/player-performance-section.inline.styles";
import {
  PlayerPerformanceSkeletonStack,
  PlayerPerformanceSkeletonWorkspace,
} from "pages/app/workspace/styles/index.inline.styles";

export const PlayerPerformanceRecordsTableSkeleton: React.FC = () => (
  <DataTableSkeleton columns={5} rows={5} showActions showThumbnail={false} />
);

export const PlayerPerformanceRosterSkeleton: React.FC = () => (
  <div aria-label="Loading players" className="space-y-2" role="status">
    {Array.from({ length: 5 }).map((_, index) => (
      <div
        className="flex items-center gap-3 rounded-[18px] border border-[var(--brand-border)] bg-[rgba(255,255,255,0.02)] px-3 py-3"
        key={index}
      >
        <SkeletonBlock className="h-10 w-10 shrink-0 rounded-2xl" />
        <div className="min-w-0 flex-1 space-y-2">
          <SkeletonBlock className="h-4 w-2/3 rounded-full" />
          <SkeletonBlock className="h-3 w-1/2 rounded-full" />
        </div>
      </div>
    ))}
  </div>
);

const PlayerPerformanceFilterBarSkeleton: React.FC<{ isAthleteAccount?: boolean }> = ({
  isAthleteAccount = false,
}) => (
  <PlayerPerformanceFilterBar>
    {isAthleteAccount ? (
      <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SkeletonBlock className="h-4 w-40 rounded-full" />
        <PlayerPerformanceFilterActions>
          <SkeletonBlock className="h-11 w-28 rounded-2xl" />
          <SkeletonBlock className="h-11 w-44 rounded-2xl" />
        </PlayerPerformanceFilterActions>
      </div>
    ) : (
      <>
        <PlayerPerformanceMobileFilterBlock>
          <SkeletonBlock className="h-3 w-24 rounded-full" />
          <PlayerPerformanceFilterInputRow>
            <SkeletonBlock className="h-[3.35rem] w-full rounded-2xl sm:flex-1" />
            <PlayerPerformanceFilterActions>
              <SkeletonBlock className="h-11 w-28 rounded-2xl" />
              <SkeletonBlock className="h-11 w-44 rounded-2xl" />
            </PlayerPerformanceFilterActions>
          </PlayerPerformanceFilterInputRow>

          <div className="rounded-[18px] border border-[var(--brand-border)] bg-[rgba(255,255,255,0.02)] px-3 py-3">
            <div className="flex items-center gap-3">
              <SkeletonBlock className="h-10 w-10 shrink-0 rounded-2xl" />
              <div className="min-w-0 flex-1 space-y-2">
                <SkeletonBlock className="h-4 w-2/3 rounded-full" />
                <SkeletonBlock className="h-3 w-1/2 rounded-full" />
              </div>
            </div>
            <SkeletonBlock className="mt-2 h-3 w-full rounded-full" />
          </div>
        </PlayerPerformanceMobileFilterBlock>

        <PlayerPerformanceDesktopFilterRow>
          <PlayerPerformanceSelectedPlayerWrap>
            <div className="flex items-center gap-3">
              <SkeletonBlock className="h-12 w-12 shrink-0 rounded-2xl" />
              <div className="min-w-0 flex-1 space-y-2">
                <SkeletonBlock className="h-4 w-40 rounded-full" />
                <SkeletonBlock className="h-3 w-28 rounded-full" />
              </div>
            </div>
            <SkeletonBlock className="mt-2 h-3 w-3/4 rounded-full" />
          </PlayerPerformanceSelectedPlayerWrap>
          <PlayerPerformanceFilterActions>
            <SkeletonBlock className="h-11 w-28 rounded-2xl" />
            <SkeletonBlock className="h-11 w-44 rounded-2xl" />
          </PlayerPerformanceFilterActions>
        </PlayerPerformanceDesktopFilterRow>
      </>
    )}
  </PlayerPerformanceFilterBar>
);

const PlayerPerformancePlayerPanelSkeleton: React.FC = () => (
  <PlayerPerformancePlayerPanel>
    <PlayerPerformancePlayerPanelHeader>
      <SkeletonBlock className="h-3 w-16 rounded-full" />
      <SkeletonBlock className="h-11 w-full rounded-2xl" />
    </PlayerPerformancePlayerPanelHeader>
    <PlayerPerformancePlayerPanelBody>
      <PlayerPerformancePlayerList>
        <PlayerPerformanceRosterSkeleton />
      </PlayerPerformancePlayerList>
    </PlayerPerformancePlayerPanelBody>
  </PlayerPerformancePlayerPanel>
);

const PlayerPerformanceRecordsPanelSkeleton: React.FC<{ isAthleteAccount?: boolean }> = ({
  isAthleteAccount = false,
}) => (
  <PlayerPerformanceRecordsPanel>
    <PlayerPerformanceFilterBarSkeleton isAthleteAccount={isAthleteAccount} />
    <PlayerPerformanceRecordsTableSkeleton />
  </PlayerPerformanceRecordsPanel>
);

export const PlayerPerformanceSectionSkeleton: React.FC<{
  isAthleteAccount?: boolean;
  showHighlights?: boolean;
}> = ({
  isAthleteAccount = false,
  showHighlights = true,
}) => (
  <PlayerPerformanceSkeletonStack aria-label="Loading performance and highlights" role="status">
    <PageHeaderSkeleton withActions={false} />

    {showHighlights && !isAthleteAccount ? (
      <SectionCardSkeleton subtitleWidthClassName="w-full max-w-md" titleWidthClassName="w-36">
        <SchoolMatchWeekHighlightsSkeletonGrid aria-label="Loading match week highlights" role="status">
          {Array.from({ length: 4 }).map((_, index) => (
            <SchoolMatchWeekHighlightSkeletonCard key={index}>
              <SchoolMatchWeekHighlightSkeletonAccent />
              <div className="mb-3 flex items-start justify-between gap-3">
                <SkeletonBlock className="h-3 w-24 rounded-full" />
                <SkeletonBlock className="h-9 w-9 rounded-[0.9rem]" />
              </div>
              <SkeletonBlock className="h-7 w-3/4 rounded-[14px]" />
              <SkeletonBlock className="mt-3 h-3 w-1/2 rounded-full" />
            </SchoolMatchWeekHighlightSkeletonCard>
          ))}
        </SchoolMatchWeekHighlightsSkeletonGrid>
      </SectionCardSkeleton>
    ) : null}

    <PlayerPerformanceSkeletonWorkspace>
      {isAthleteAccount ? (
        <PlayerPerformanceRecordsPanelSkeleton isAthleteAccount />
      ) : (
        <PlayerPerformanceMasterDetailGrid>
          <PlayerPerformancePlayerPanelSkeleton />
          <PlayerPerformanceRecordsPanelSkeleton />
        </PlayerPerformanceMasterDetailGrid>
      )}
    </PlayerPerformanceSkeletonWorkspace>
  </PlayerPerformanceSkeletonStack>
);
